import _ from 'lodash';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { ActionsBanner, ColumnsDrawer, ExportDialog, Page } from '~/components';
import { BulkActionMenu } from '~/components/bulk-action';
import { useApi, useConfirmation, useSubscription, useWorkspace } from '~/contexts';
import {
  useAuth,
  useClientFilters,
  useDocumentTitle,
  useFeatures,
  useIsMounted,
  useProjectFilters,
  useRowSelection,
  useSearchParams,
  useSearchParamsConfig,
  useViewColumns,
} from '~/hooks';
import projectRecordStatuses from '~/lookups/project-record-statuses.js';
import EditTimeEntry from '~/routes/app/time/edit-time-entry';
import { PageLoader } from '~/routes/public/pages';
import { QuerySort, dateFormats } from '~/utils';
import useReportsSearchParamsConfig from '../reports/hooks/useReportsSearchParamsConfig.js';
import { BulkArchive, BulkDelete, BulkSetStatus, BulkUnarchive } from './bulk';
import ProjectDeleteConfirmation from './ProjectDeleteConfirmation';
import ProjectsListFilters from './ProjectsListFilters';
import ProjectsTable from './ProjectsTable';

export default function ProjectsListPage({ clientId, memberId, mode = 'page', renderFilters, sessionKey }) {
  const documentTitle = useDocumentTitle();
  const { url } = useRouteMatch();
  useEffect(() => {
    if (mode === 'page') documentTitle.set('Projects');
  }, [mode, documentTitle]);

  const { workspace } = useWorkspace();
  const api = useApi();
  const auth = useAuth();

  const isMounted = useIsMounted();

  const searchParamsConfig = useSearchParamsConfig();
  const reportsSearchParamsConfig = useReportsSearchParamsConfig();

  const clientFilters = useClientFilters();
  const projectFilters = useProjectFilters({
    projectRecordStatusId: { default: projectRecordStatuses.active.id, ...searchParamsConfig.projectRecordStatusId },
  });

  const history = useHistory();
  const location = useLocation();

  const [timeEntryDrawer, setTimeEntryDrawer] = useState(null);
  const { notify } = useSubscription();

  const [query, setQuery] = useState({
    isReady: false,
    searchParamsStatus: 'pending',
    data: null,
    params: {
      q: '',
      sort: new QuerySort('name', 'asc'),
      page: 0,
      size: 25,
      startedDatePeriod: null,
      endedDatePeriod: null,
      createdAtPeriod: null,
      completedOnPeriod: null,
      projectBudgetHealth: [],
      projectScheduleHealth: [],
      projectClientSatisfaction: [],
      projectTeamSatisfaction: [],
      ...clientFilters.filters,
      ...projectFilters.filters,
    },
    action: 'load',
  });

  const setParams = (params) => {
    setQuery((state) => ({
      ...state,
      action: 'filter',
      params: { ...state.params, ...params, page: 0 },
      searchParamsStatus: 'ready',
    }));
  };

  const loadMore = useCallback(() => {
    setQuery((state) => {
      if (
        state.searchParamsStatus !== 'ready' ||
        state.action !== null ||
        !state.data ||
        state.data.total <= state.data.results.length
      ) {
        return state;
      }

      return {
        ...state,
        params: { ...state.params, page: state.params.page + 1 },
        action: 'load-more',
      };
    });
  }, []);

  const removeItem = (id) => {
    setQuery((state) => ({
      ...state,
      data: {
        ...state.data,
        results: state.data?.results.filter((i) => i.id !== id),
        total: state.data.total - 1,
      },
    }));
  };

  const updateItem = (item) => {
    setQuery((state) => ({
      ...state,
      data: {
        ...state.data,
        results: state.data?.results.map((i) => (i.id === item.id ? { ...i, ...item } : i)),
      },
    }));
  };

  const { action, data, isReady, params, searchParamsStatus } = query;

  const selection = useRowSelection({ rows: data?.results });
  const showBulkSelection = auth.projects.edit || auth.projects.delete;
  const selectedProjects = useMemo(
    () => (data?.results ?? []).filter((project) => selection.ids.includes(project.id)),
    [data?.results, selection.ids],
  );

  const confirmation = useConfirmation();

  const searchParams = useSearchParams({
    config: useMemo(
      () => ({
        q: { default: '' },
        sort: { default: new QuerySort('name', 'asc'), ...searchParamsConfig.sort },
        startedDatePeriod: reportsSearchParamsConfig.startPeriod,
        endedDatePeriod: reportsSearchParamsConfig.endPeriod,
        createdAtPeriod: reportsSearchParamsConfig.createdPeriod,
        completedOnPeriod: reportsSearchParamsConfig.completedPeriod,
        projectBudgetHealth: searchParamsConfig.projectBudgetHealth,
        projectScheduleHealth: searchParamsConfig.projectScheduleHealth,
        projectClientSatisfaction: searchParamsConfig.projectClientSatisfaction,
        projectTeamSatisfaction: searchParamsConfig.projectTeamSatisfaction,
        ...clientFilters.searchParamsConfig,
        ...projectFilters.searchParamsConfig,
      }),
      [reportsSearchParamsConfig, searchParamsConfig, clientFilters, projectFilters],
    ),

    sessionKey,

    onChange: useCallback((params) => setParams((state) => ({ ...state, ...params })), []),
  });

  const urlSearchParams = useMemo(
    () => ({
      q: params.q,
      sort: params.sort,
      page: params.page,
      size: params.size,
      memberId,
      startFrom: params.startedDatePeriod?.start ?? undefined,
      startTo: params.startedDatePeriod?.end ?? undefined,
      endFrom: params.endedDatePeriod?.start ?? undefined,
      endTo: params.endedDatePeriod?.end ?? undefined,
      createdStart: params.createdAtPeriod?.start ?? undefined,
      createdEnd: params.createdAtPeriod?.end ?? undefined,
      completedOnFrom: params.completedOnPeriod?.start ?? undefined,
      completedOnTo: params.completedOnPeriod?.end ?? undefined,
      projectBudgetHealthId: params.projectBudgetHealth?.map((v) => v.id),
      projectScheduleHealthId: params.projectScheduleHealth?.map((v) => v.id),
      projectClientSatisfactionId: params.projectClientSatisfaction?.map((v) => v.id),
      projectTeamSatisfactionId: params.projectTeamSatisfaction?.map((v) => v.id),
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      ...clientFilters.mapUrlSearchParams(params),
      ...projectFilters.mapUrlSearchParams(params),
    }),
    [params, clientFilters, projectFilters, memberId],
  );

  useEffect(() => {
    if (searchParamsStatus !== 'pending') return;
    searchParams.get().then((params) => {
      if (params) setParams(params);
    });
  }, [searchParams, searchParamsStatus]);

  const fetchData = useCallback(async () => {
    try {
      const params = {
        ...urlSearchParams,
        clientId: urlSearchParams.clientId.length ? urlSearchParams.clientId : clientId,
      };

      const { data } = await api.www.workspaces(workspace.id).projects().get(params);

      if (!isMounted.current) return;

      setQuery((state) => ({
        ...state,
        action: null,
        searchParamsStatus: 'ready',
        data: {
          ...data,
          results: state.action === 'load-more' ? [...state.data.results, ...data.results] : data.results,
          total: data.total,
        },
      }));
    } catch (error) {
      setQuery((state) => ({ ...state, data: { total: 0, results: [] } }));
    }
  }, [workspace.id, api, isMounted, urlSearchParams, clientId]);

  useEffect(() => {
    if (searchParamsStatus !== 'ready') return;
    fetchData();
  }, [fetchData, searchParamsStatus]);

  const handleFilter = (values) => {
    if (values !== params) {
      selection.clear();
    }
    setParams({ ...values });
    searchParams.set({ ..._.omit(values, 'sort') });
  };

  const handleSort = ({ column, sort }) => {
    const direction = column === sort.column && sort.direction === 'asc' ? 'desc' : 'asc';
    const querySort = new QuerySort(column, direction);
    setParams({ sort: querySort });
    searchParams.set({ sort: querySort });
    selection.clear();
  };

  async function handleSaved(project) {
    const { data } = await api.www.workspaces(workspace.id).projects().get({ ids: project.id });
    updateItem(data[0]);
  }

  async function handleDeleted(project) {
    removeItem(project.id);
  }

  async function handleDeleteConfirmation(project) {
    confirmation.prompt((resolve) => (
      <ProjectDeleteConfirmation
        project={project}
        onClose={resolve}
        onDelete={() => {
          handleDeleted(project);
          resolve(true);
        }}
      />
    ));
  }

  const handleBulkDelete = async () => {
    await confirmation.prompt((resolve) => (
      <BulkDelete
        ids={selection.ids}
        onComplete={async () => {
          selection.clear();
          if (params.page !== 0) {
            await fetchData();
          } else {
            setParams({ page: 0 });
          }
        }}
        onClose={resolve}
      />
    ));
  };

  const handleBulkArchive = async () => {
    await confirmation.prompt((resolve) => (
      <BulkArchive
        ids={selection.ids}
        projects={selectedProjects}
        onComplete={async () => {
          selection.clear();
          if (params.page !== 0) {
            await fetchData();
          } else {
            setParams({ page: 0 });
          }
        }}
        onClose={resolve}
      />
    ));
  };

  const handleBulkUnarchive = async () => {
    await confirmation.prompt((resolve) => (
      <BulkUnarchive
        ids={selection.ids}
        onComplete={async () => {
          selection.clear();
          if (params.page !== 0) {
            await fetchData();
          } else {
            setParams({ page: 0 });
          }
        }}
        onClose={resolve}
      />
    ));
  };

  const handleBulkSetStatus = async () => {
    await confirmation.prompt((resolve) => (
      <BulkSetStatus
        ids={selection.ids}
        onComplete={async () => {
          selection.clear();
          if (params.page !== 0) {
            await fetchData();
          } else {
            setParams({ page: 0 });
          }
        }}
        onClose={resolve}
      />
    ));
  };

  const handleExport = async (filename, mimeType) => {
    await confirmation.prompt((resolve) => (
      <ExportDialog
        filename={filename}
        onLoad={api.www
          .workspaces(workspace.id)
          .projects()
          .export(urlSearchParams, {
            headers: { accept: mimeType },
            responseType: 'blob',
          })}
        onClose={resolve}
      />
    ));
  };

  const [dialog, setDialog] = useState(null);

  const features = useFeatures();

  const columns = useViewColumns({
    storageKey: 'projects_list_columns',
    columns: useMemo(
      () =>
        _.compact([
          { key: 'name', name: 'Name', disabled: true },
          { key: 'code', name: 'Code', default: true },
          { key: 'status', name: 'Status', default: true },
          { key: 'projectAdmin', name: 'Admins', default: true },
          { key: 'billingType', name: 'Billing Type', default: true },
          features.practices && { key: 'practice', name: 'Practice', default: false },
          features.businessUnits && { key: 'businessUnit', name: 'Business Unit', default: false },
          { key: 'salesRepresentative', name: 'Sales Representative', default: false },
          { key: 'projectType', name: 'Type', default: false },
          { key: 'start', name: 'Start Date', default: false },
          { key: 'end', name: 'End Date', default: false },
          { key: 'currency', name: 'Currency', default: false },
          { key: 'health', name: 'Health', default: true },
          { key: 'createdAt', name: 'Created At', default: true },
        ]),
      [features],
    ),
  });

  const handleShowColumnsDrawer = () => {
    setDialog(
      <ColumnsDrawer
        value={columns.selected}
        columns={columns.all}
        onClose={() => setDialog(null)}
        onApply={(value) => {
          columns.save(value);
          setDialog(null);
        }}
      />,
    );
  };

  const Container = useCallback(
    (props) => (mode === 'page' ? <Page {...props} /> : <React.Fragment {...props} />),
    [mode],
  );

  if (!isReady && !data) return <PageLoader />;

  return (
    <>
      <Container>
        {renderFilters ? (
          renderFilters({ query, onFilter: handleFilter })
        ) : (
          <ProjectsListFilters
            onChange={handleFilter}
            onExport={handleExport}
            onShowColumnsDrawer={handleShowColumnsDrawer}
            params={params}
          />
        )}

        <Page.ListView>
          <ProjectsTable
            data={data}
            params={params}
            columns={columns}
            mode={mode}
            action={action}
            selection={selection}
            showBulkSelection={showBulkSelection}
            onRowClick={({ key, client }) =>
              history.push(`/app/${workspace.key}/portfolio/projects/${client.key}/${key}`)
            }
            onEdit={(project, event) => {
              const pathname = `/app/${workspace.key}/portfolio/projects/${project.client.key}/${project.key}/settings`;
              if (event?.metaKey || event?.ctrlKey) {
                window.open(pathname + location.search, '_blank', 'noopener');
                return;
              }
              history.push({ pathname, search: location.search, state: { scrollToTop: false } });
            }}
            onClone={(project) =>
              history.push({
                pathname: `${url}/clone/${project.client.key}/${project.key}`,
                search: location.search,
                state: { scrollToTop: false },
              })
            }
            onDelete={handleDeleteConfirmation}
            onSaved={handleSaved}
            onSort={handleSort}
            onDrawerClose={() => documentTitle.set('Projects')}
            onTrackTime={(value) => setTimeEntryDrawer(value)}
            onLoadMore={loadMore}
          />
        </Page.ListView>

        {showBulkSelection && selection.any && (
          <ActionsBanner>
            <BulkActionMenu count={selection.count}>
              {auth.projects.edit && (
                <BulkActionMenu.Item onClick={() => handleBulkSetStatus()}>Set Status</BulkActionMenu.Item>
              )}
              {auth.projects.delete && (
                <BulkActionMenu.Item onClick={() => handleBulkArchive()}>Archive</BulkActionMenu.Item>
              )}
              {auth.projects.delete && (
                <BulkActionMenu.Item onClick={() => handleBulkUnarchive()}>Unarchive</BulkActionMenu.Item>
              )}
              {auth.projects.delete && (
                <BulkActionMenu.Item onClick={() => handleBulkDelete()}>Delete</BulkActionMenu.Item>
              )}
            </BulkActionMenu>
          </ActionsBanner>
        )}
      </Container>

      {timeEntryDrawer && (
        <EditTimeEntry
          initialValues={{ projectId: timeEntryDrawer.project.id, date: moment().format(dateFormats.isoDate) }}
          onClose={() => {
            setTimeEntryDrawer(null);
            documentTitle.set('Projects');
          }}
          onSaved={(timeEntry) => {
            notify(useSubscription.keys.refresh_timer);
            handleSaved(timeEntry.project);
          }}
        />
      )}

      {dialog}
    </>
  );
}
